import { memo, useEffect, useRef, useState } from 'react'
import { LoadingFullIndicator, LoadingGlobalIndicatorPortal } from './index'
import { useIsFetching } from '@tanstack/react-query'
import { KEYS } from '@/react-query'
import { useSelector } from 'react-redux'
import { selectSettingsIsSkeleton } from '@/store/settings'
import { useSkeletonOnlyPath } from '@/hooks'
import { AnimatePresence } from 'framer-motion'
import { MotionDiv } from '@/components'

interface GlobalIndicatorProps {
  useDelay: boolean;
  imageSrc: string;
  rootElementSelector: string;
}

interface TrackingIndicatorProps  {
  useDelay?: boolean;
  imageSrc: string;
}

interface IndicatorProps {
  isShowing: boolean;
  imageSrc: string;
  duration?: number;
}

const sheetVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}
const timing = { duration: 0.2, ease: 'easeInOut' as const }

const LoadingGlobalIndicator = memo(function LoadingGlobalIndicator(props: GlobalIndicatorProps) {
  const { useDelay, imageSrc, rootElementSelector } = props

  const isSkeleton = useSelector(selectSettingsIsSkeleton)
  const isSkeletonOnlyPath = useSkeletonOnlyPath()

  const hideGlobalIndicator = isSkeleton || isSkeletonOnlyPath

  return (
    <LoadingGlobalIndicatorPortal rootElementSelector={rootElementSelector}>
      {!hideGlobalIndicator && <PromiseTrackingIndicator useDelay={useDelay} imageSrc={imageSrc}/>}
    </LoadingGlobalIndicatorPortal>
  )
})

const PromiseTrackingIndicator = memo(function PromiseTrackingIndicator(props: TrackingIndicatorProps) {
  const { useDelay = false, imageSrc } = props

  const isUbpayListFetching = useIsFetching({ queryKey: [KEYS.UNPAID_TOLLS_LIST()] }) // 미납통행료내역 (글로벌 로딩 인디케이터 사용)

  const isFetching = useIsFetching({
    predicate: (query) => {
      // excludeFromGlobalLoading 플래그가 true가 아닌 쿼리만 카운트(미납통행료내역 페이지에서는 로딩이 필요하니 예외 처리)
      return !query.meta?.excludeFromGlobalLoading || (isUbpayListFetching > 0)
    },
  })

  const [isShowing, setIsShowing] = useState(isFetching > 0)

  const showTimeoutRef = useRef<number | null>(null)
  const hideTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    const SHOW_DELAY = useDelay ? 150 : 0     // 나타나기 전 딜레이
    const MIN_VISIBLE = 400                  // 최소 노출 시간(ms)

    // 항상 타이머 클리어
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    if (isFetching > 0) {
      // 로딩 시작: 일정 시간 지속되면 보여주기
      if (!isShowing) {
        showTimeoutRef.current = window.setTimeout(() => {
          setIsShowing(true)
          showTimeoutRef.current = null
        }, SHOW_DELAY)
      }
    } else {
      // 로딩 끝: 바로 끄지 말고 최소 시간 유지
      if (isShowing) {
        hideTimeoutRef.current = window.setTimeout(() => {
          setIsShowing(false)
          hideTimeoutRef.current = null
        }, MIN_VISIBLE)
      }
    }

    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current)
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    }
  }, [isFetching, isShowing, useDelay])

  return <Indicator isShowing={isShowing} imageSrc={imageSrc} />
})

export const Indicator = memo(function Indicator(props: IndicatorProps) {
  const { isShowing, imageSrc } = props
  const ref = useRef(null)

  return (
    <AnimatePresence initial={false}>
      {isShowing && (
        <MotionDiv
          variants={sheetVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={timing}
        >
          <LoadingFullIndicator
            imageSrc={imageSrc}
            ref={ref}
          />
        </MotionDiv>
      )}
    </AnimatePresence>
  )
})

export { LoadingGlobalIndicator }
